import { useEffect, useRef, useState } from 'react';
import { useBuscarPrazos } from '../hooks';
import type { Etiqueta, PrazoCard } from '../types';
import { fmtData, VAR_COR } from '../utils';

interface Props {
  /** Chamado quando o usuário escolhe um resultado — cada tela navega no seu contexto. */
  onSelect: (card: PrazoCard) => void;
  placeholder?: string;
  /** Rodapé curto explicando o que acontece ao escolher (ex.: "vai para a semana"). */
  dica?: string;
  larguraInput?: string;  // classes tailwind de largura (default w-44 sm:w-56)
}

/**
 * Busca de processo por número, transversal às telas. Consulta o backend
 * (`/api/prazos/buscar`) em QUALQUER data — debounce de 300ms, dropdown com os
 * matches (nº, tipo, tribunal e data). Ao escolher, dispara `onSelect`; a tela
 * decide o que fazer (pular para a semana/mês, abrir o detalhe, etc.).
 */
export function BuscaProcesso({ onSelect, placeholder = 'Buscar processo nº…', dica, larguraInput = 'w-44 sm:w-56' }: Props) {
  const [texto, setTexto] = useState('');
  const [debounced, setDebounced] = useState('');
  const [aberto, setAberto] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(texto), 300);
    return () => clearTimeout(t);
  }, [texto]);

  const { data, isFetching } = useBuscarPrazos(debounced);
  const resultados = data ?? [];
  const digitos = texto.replace(/\D/g, '');

  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setAberto(false);
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  function escolher(c: PrazoCard) {
    onSelect(c);
    setAberto(false);
    setTexto('');
    setDebounced('');
  }

  const mostrar = aberto && digitos.length >= 3;

  return (
    <div ref={boxRef} className="relative">
      <input
        type="search"
        value={texto}
        onChange={(e) => { setTexto(e.target.value); setAberto(true); }}
        onFocus={() => setAberto(true)}
        onKeyDown={(e) => { if (e.key === 'Enter' && resultados.length > 0) escolher(resultados[0]); }}
        placeholder={placeholder}
        title="Localiza o processo em qualquer data e leva você até ele"
        className={`${larguraInput} pl-7 pr-2 py-1.5 text-xs rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 text-gray-700 dark:text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-400`}
      />
      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs pointer-events-none">🔍</span>

      {mostrar && (
        <div className="absolute z-40 mt-1 w-72 right-0 max-h-80 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-xl">
          {isFetching && resultados.length === 0 && (
            <div className="px-3 py-2 text-xs text-gray-400">buscando…</div>
          )}
          {!isFetching && resultados.length === 0 && (
            <div className="px-3 py-2 text-xs text-gray-400">nenhum processo encontrado</div>
          )}
          {resultados.map((c) => (
            <button
              key={c.id_prazo}
              onClick={() => escolher(c)}
              className="w-full text-left px-3 py-2 hover:bg-blue-50 dark:hover:bg-blue-900/30 border-b border-gray-100 dark:border-gray-800 last:border-0 flex items-center gap-2"
            >
              <span className="inline-block w-2 h-2 rounded-full shrink-0" style={{ background: VAR_COR[c.etiqueta as Etiqueta] }} />
              <span className="flex-1 min-w-0">
                <span className="block text-xs font-medium text-gray-800 dark:text-gray-100 truncate">{c.numero_processo}</span>
                <span className="block text-[10px] text-gray-500 dark:text-gray-400 truncate">
                  {c.tipo_prazo}{c.sigla_tribunal ? ` · ${c.sigla_tribunal}` : ''}
                </span>
              </span>
              <span className="text-[10px] text-gray-500 dark:text-gray-400 whitespace-nowrap">
                {fmtData(c.data_evento ?? c.data_vencimento)}
              </span>
            </button>
          ))}
          {dica && resultados.length > 0 && (
            <div className="px-3 py-1.5 text-[10px] text-gray-400 bg-gray-50 dark:bg-gray-950/40 sticky bottom-0">{dica}</div>
          )}
        </div>
      )}
    </div>
  );
}
